// 應用配置統一管理
import { LanguageManager, SUPPORTED_LANGUAGES, DEFAULT_LANGUAGE } from './translations';
import { ThemeManager, SUPPORTED_THEMES, DEFAULT_THEME } from './themes';
import { devLog } from '../utils/devLog';

// 應用基本資訊
export const APP_INFO = {
  name: "ASCII Drum Editor",
  version: "2.0.0",
  description: "Create and play drum patterns with ASCII visualization",
  author: "ASCII Drum Team",
  repository: "https://github.com/username/ascii-drum-editor",
  license: "GPL-2.0"
};

// 預設設定
export const DEFAULT_SETTINGS = {
  // 音樂設定
  timeSignature: "4/4",
  noteDivision: "16n",
  tempo: 120,
  loopCount: 1,
  
  // 界面設定
  language: DEFAULT_LANGUAGE,
  theme: DEFAULT_THEME,
  advancedMode: false,
  
  // 顯示設定
  showStepNumbers: true,
  showInstrumentLabels: true,
  autoSave: true,
  
  // 音訊設定
  audioEnabled: true,
  volume: 0.8,
  audioFormat: "wav"
};

// 應用狀態鍵名
export const STORAGE_KEYS = {
  LANGUAGE: 'language',
  THEME: 'theme',
  DRUMSET_CONFIG: 'drumsetConfig',
  USER_SETTINGS: 'userSettings',
  RECENT_PATTERNS: 'recentPatterns',
  LAST_SESSION: 'lastSession'
};

// 支援的檔案格式
export const SUPPORTED_FORMATS = {
  export: [
    { 
      extension: '.json', 
      name: 'JSON Pattern', 
      description: 'ASCII Drum Pattern with drumset configuration' 
    }
  ],
  import: [
    { 
      extension: '.json', 
      name: 'JSON Pattern/Drumset', 
      description: 'Pattern files or drumset configurations' 
    }
  ]
};

// 應用管理器
export const AppManager = {
  // 初始化應用
  initialize: () => {
    // 載入用戶設定
    const settings = AppManager.getSettings();
    
    // 設定語言
    LanguageManager.setLanguage(settings.language);
    
    // 設定主題
    ThemeManager.setTheme(settings.theme);
    
    devLog(`${APP_INFO.name} v${APP_INFO.version} initialized`);
    return settings;
  },
  
  // 獲取用戶設定
  getSettings: () => {
    try {
      const saved = localStorage.getItem(STORAGE_KEYS.USER_SETTINGS);
      if (saved) {
        const parsed = JSON.parse(saved);
        return { ...DEFAULT_SETTINGS, ...parsed };
      }
    } catch (error) {
      console.warn('Error loading user settings:', error);
    }
    return { ...DEFAULT_SETTINGS };
  },
  
  // 儲存用戶設定
  saveSettings: (settings: any) => {
    try {
      const current = AppManager.getSettings();
      const updated = { ...current, ...settings };
      localStorage.setItem(STORAGE_KEYS.USER_SETTINGS, JSON.stringify(updated));
      return updated;
    } catch (error) {
      console.error('[AppManager] Failed to save settings:', error);
      return null;
    }
  },
  
  // 重設設定
  resetSettings: () => {
    try {
      localStorage.removeItem(STORAGE_KEYS.USER_SETTINGS);
      return true;
    } catch (error) {
      console.error('[AppManager] Failed to reset settings:', error);
      return false;
    }
  },
  
  // 匯出設定
  exportSettings: () => {
    try {
      const settings = AppManager.getSettings();
      const exportData = {
        type: "settings",
        version: "1.0",
        timestamp: new Date().toISOString(),
        settings: settings
      };
      
      const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `drum-editor-settings-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      return true;
    } catch (error) {
      console.error('[AppManager] Failed to export settings:', error);
      return false;
    }
  },
  
  // 匯入設定
  importSettings: (file: File) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          if (!e.target?.result) {
            reject(new Error('No file content'));
            return;
          }
          const data = JSON.parse(e.target.result as string);

          if (data.type === "settings" && data.settings) {
            // 匯入用戶設定
            AppManager.saveSettings(data.settings);
            resolve({ success: true, type: "settings", data: data.settings });
          } else {
            reject(new Error('Invalid file format'));
          }
        } catch (error) {
          reject(error);
        }
      };
      reader.readAsText(file);
    });
  },
  
  // 獲取應用資訊
  getAppInfo: () => APP_INFO,
  
  // 獲取支援的語言
  getSupportedLanguages: () => SUPPORTED_LANGUAGES,
  
  // 獲取支援的主題
  getSupportedThemes: () => SUPPORTED_THEMES,
  
  // 獲取支援的檔案格式
  getSupportedFormats: () => SUPPORTED_FORMATS,
  
  // 儲存最近的模式
  saveRecentPattern: (patternData: any) => {
    try {
      const recent = JSON.parse(localStorage.getItem(STORAGE_KEYS.RECENT_PATTERNS) || '[]');
      
      // 添加時間戳
      const patternWithTimestamp = {
        ...patternData,
        savedAt: new Date().toISOString(),
        id: Date.now().toString()
      };
      
      // 保持最近10個
      recent.unshift(patternWithTimestamp);
      const trimmed = recent.slice(0, 10);
      
      localStorage.setItem(STORAGE_KEYS.RECENT_PATTERNS, JSON.stringify(trimmed));
      return true;
    } catch (error) {
      console.error('Error saving recent pattern:', error);
      return false;
    }
  },
  
  // 獲取最近的模式
  getRecentPatterns: () => {
    try {
      const recent = localStorage.getItem(STORAGE_KEYS.RECENT_PATTERNS);
      return recent ? JSON.parse(recent) : [];
    } catch (error) {
      console.error('Error loading recent patterns:', error);
      return [];
    }
  },
  
  // 清除最近的模式
  clearRecentPatterns: () => {
    try {
      localStorage.removeItem(STORAGE_KEYS.RECENT_PATTERNS);
      return true;
    } catch (error) {
      console.error('Error clearing recent patterns:', error);
      return false;
    }
  }
};

// 鍵盤快捷鍵配置
export const KEYBOARD_SHORTCUTS = {
  // 播放控制
  PLAY_PAUSE: 'Space',
  STOP: 'Escape',
  
  // 檔案操作
  EXPORT: 'Ctrl+E',
  IMPORT: 'Ctrl+O',
  SAVE: 'Ctrl+S',
  
  // 編輯操作
  CLEAR_ALL: 'Ctrl+D',
  UNDO: 'Ctrl+Z',
  REDO: 'Ctrl+Y',
  
  // 視圖切換
  TOGGLE_ADVANCED: 'Ctrl+A',
  TOGGLE_THEME: 'Ctrl+T',
  
  // 導航
  DRUMSET_EDITOR: 'Ctrl+1',
  PATTERN_EDITOR: 'Ctrl+2'
};

// 效能配置
export const PERFORMANCE_CONFIG = {
  // 音訊緩衝
  audioBufferSize: 4096,
  audioLookahead: 0.1,
  
  // 渲染優化
  maxStepsPerBar: 64,
  maxInstruments: 16,
  
  // 自動儲存間隔（毫秒）
  autoSaveInterval: 30000,
  
  // 防抖延遲（毫秒）
  debounceDelay: 300,
  
  // 音檔預載
  preloadAudio: true,
  audioPreloadTimeout: 5000
}; 
